import React from "react";
import { useDropzone } from "react-dropzone";
import InlineEdit from "@atlaskit/inline-edit";
import { ExtendedFieldProps } from "@atlaskit/inline-edit/dist/types/types";

import { Grid } from "../../UI/Grid";
import { L2Selector } from "../../common/network/NetworkSelectors";
import { ReadOnlyWrapper } from "../../components/ReadOnlyWrapper";
import { Switch } from "../../components/Switch";
import { useUserPermissions } from "../../hooks/useUserPermissions";
import { useNetwork } from "../../hooks/useNetwork";
import { readCsvOrExcel } from "../../common/csv_utils";
import { Paper } from "../../UI/Paper";
import { List } from "../../UI/List";
import { Skeleton } from "../../UI/Skeleton";
import { ListItem } from "../../UI/ListItem";
import { COLOR_ATHENS_GRAY } from "../../constants/styles";
import { Typography } from "../../UI/Typography";
import { TextField } from "../TextField";
import { Box } from "../../UI/Box";
import { RecordChangeConfirmModal } from "./RecordChangeConfirmModal";
import { useModalContext } from "../../contexts/ModalContext/context";
import { UploadButton } from "../UploadButton";
import { useSnackbar } from "../../hooks/useSnackbar";
import { HousingAndMajorsReplacementModal } from "../HousingAndMajorsReplacementModal";
import useAuth from "../../routes/login/AuthContext";

const getSwitchLabel = (active: boolean) => (active ? "Active" : "Not Active");

interface IEntry {
  id: number;
  name: string;
}

interface IInitialState {
  active: boolean;
  entries: IEntry[];
}

export interface IProps {
  objectType: any;
  handleObjectChange: any;
  handleObjectAdd?: any;
  handleActivationChange: any;
  loadData: any;
}

const initialState: IInitialState = { active: false, entries: [] };

const LIST_ITEM_HEIGHT = 44;

const getListItemSX = (idx: number) => ({
  height: LIST_ITEM_HEIGHT,
  backgroundColor: idx % 2 ? COLOR_ATHENS_GRAY : undefined,
  "& form > div": {
    marginTop: 0,
  },
});

const valudateEditInput = (value: string) => {
  if (value.length === 0) {
    return "Update input can't be empty";
  }

  if (value.length > 50) {
    return "Must be less than 50 characters long";
  }
};

export const HousingMajors: React.FC<IProps> = (props) => {
  const { readOnly } = useUserPermissions();
  const { isWriteAccess, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar } = useSnackbar();
  const { setIsOpen } = useModalContext();
  const [rowId, setRowId] = React.useState<number>();
  const [oldValue, setOldValue] = React.useState("");
  const [newValue, setNewValue] = React.useState("");
  const [subnetwork, setSubnetwork] = React.useState<number | null>(null);
  const [data, setData] = React.useState(initialState);
  const [uploadValues, setUploadValues] = React.useState([]);
  const { selectedNetwork, getL2 } = useNetwork();
  const [loading, setLoading] = React.useState(false);
  const [overwriteable, setOverwriteable] = React.useState(false);

  const [uploadModalShown, setUploadModalShown] = React.useState(false);

  const l2 = React.useMemo(() => subnetwork && getL2(selectedNetwork.l1Id, subnetwork), [
    selectedNetwork.l1Id,
    subnetwork,
    getL2,
  ]);

  const isReadOnly = React.useMemo(() => !!(l2 && (l2.matchingRoundStatus === "ARCHIVED" || l2.matchingRoundStatus === "CLOSED")), [l2]);


  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: {
      // @ts-ignore
      "text/csv": [".csv", ".xlsx"]
    },
  });

  const [activateChange, setActivateChange] = React.useState<boolean | null>(null);

  const handleModalConfirm = async () => {
    const updatedObject = await props.handleObjectChange(
      subnetwork,
      oldValue,
      newValue === "" ? oldValue : newValue,
      rowId
    );

    const newData = { ...data };

    let indexToReplace: any;

    newData.entries.filter((el: IEntry, index) => {
      if (el.id === rowId) {
        indexToReplace = index;
      }
      return el.id === rowId;
    });

    newData.entries[indexToReplace] = updatedObject.result;
    setData(newData);

    setIsOpen(false);
  };

  const handleEditConfirm = (entry: IEntry) => (newValue: string) => {
    if (entry.name === newValue) {
      return;
    }

    setOldValue(entry.name);
    setNewValue(newValue);
    setRowId(entry.id);

    if (overwriteable) {
      handleModalConfirm();
    } else {
      setIsOpen(true);
    }
  };

  const handleError = ({ reason }: { reason: string | null }) =>
    showErrorSnackbar(reason || "We can only accept one file");

  // onFilesUploaded
  React.useEffect(() => {
    if (acceptedFiles) {
      if (acceptedFiles.length > 1) {
        showErrorSnackbar("We can only accept one file");
        return;
      }
      if (acceptedFiles.length === 1) {
        readCsvOrExcel(acceptedFiles[0], setUploadValues, handleError);
      }
    }
  }, [acceptedFiles]);

  // we parsed some uploaded values correctly
  React.useEffect(() => {
    if (uploadValues.length > 0) {
      setUploadModalShown(true);
    }
  }, [uploadValues]);

  // on l1 changed
  React.useEffect(() => {
    setSubnetwork(null);
    setData(initialState);
  }, [selectedNetwork.l1Id]);

  function loadTypeData() {
    const load = async () => {
      const newData = await props.loadData(subnetwork);
      setData(newData);
      setTimeout(() => {
        setLoading(false);
      }, 800);
    };

    // because if(0) is false
    if (subnetwork || subnetwork === 0) {
      setLoading(true);
      load();
    }
  }

  // on l2 changed
  React.useEffect(() => {
    loadTypeData();

    if (!subnetwork) {
      return;
    }

    const subnetworkInfo = getL2(selectedNetwork.l1Id, subnetwork);

    if (subnetworkInfo != null) {
      setOverwriteable(data.active && ((data.entries.length === 0 && subnetworkInfo.usersInNetwork > 0) || !(subnetworkInfo.usersInNetwork > 0)));
    }
  }, [subnetwork, data.entries.length, selectedNetwork.l1Id, data.active]);

  // on l2 activation
  React.useEffect(() => {
    const makeChange = async () => {
      const success = await props.handleActivationChange(subnetwork, !!activateChange);

      if (success) {
        setData({ ...data, active: !!activateChange });
      }
      setActivateChange(null);

      if (success && activateChange) {
        loadTypeData();
      }
    };

    if (activateChange !== null) {
      makeChange();
    }
  }, [activateChange]);

  const editView = (isDisabled: boolean) => ({ errorMessage, ...fieldProps }: ExtendedFieldProps<string>) => (
    <TextField {...fieldProps} autoFocus isDisabled={isDisabled } />
  );

  const readView = (name: string) => () => <Box>{name || "Click to enter a value"}</Box>;

  const handleActiveChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setActivateChange(checked);
  };

  return (
    <Grid container spacing={3}>
      <Grid item container spacing={3} alignItems="flex-end">
        <Grid item minWidth={280}>
          <L2Selector onSelectHandler={setSubnetwork} />
        </Grid>
        {!!subnetwork && (
          <Grid item xs>
            <ReadOnlyWrapper>
              <Switch
                isDisabled={isSuperAdminRead() || isReadOnly}
                label={getSwitchLabel(data.active)}
                switchProps={{
                  checked: data.active,
                  onChange: handleActiveChange,
                }}
              />
            </ReadOnlyWrapper>
          </Grid>
        )}
      </Grid>
      {!!subnetwork && !!data.active && (
        <Grid item xs={12}>
          <Paper>
            <List>
              {loading && (
                <>
                  <ListItem sx={{ backgroundColor: COLOR_ATHENS_GRAY }}>
                    <Skeleton variant="rectangular" width="100%" height={LIST_ITEM_HEIGHT - 16} />
                  </ListItem>
                  <ListItem>
                    <Skeleton variant="rectangular" width="100%" height={LIST_ITEM_HEIGHT - 16} />
                  </ListItem>
                  <ListItem sx={{ backgroundColor: COLOR_ATHENS_GRAY }}>
                    <Skeleton variant="rectangular" width="100%" height={LIST_ITEM_HEIGHT - 16} />
                  </ListItem>
                </>
              )}
              {!data.entries.length && !loading && (
                <ListItem>
                  <Typography variant="body2">No entries</Typography>
                </ListItem>
              )}
              {!loading &&
                data.entries.map((entry: IEntry, idx: number) => (
                  <ListItem key={entry.id} sx={getListItemSX(idx)}>
                    <ReadOnlyWrapper>
                      <InlineEdit
                        defaultValue={entry.name}
                        validate={valudateEditInput}
                        readView={readView(entry.name)}
                        onConfirm={handleEditConfirm(entry)}
                        editView={editView(isSuperAdminRead() || isReadOnly)}
                      />
                    </ReadOnlyWrapper>
                  </ListItem>
                ))}
            </List>
          </Paper>
          <RecordChangeConfirmModal handleConfirm={handleModalConfirm} />
        </Grid>
      )}
      {overwriteable && !readOnly && !isReadOnly && isWriteAccess() && (
        <Grid item xs={12}>
          <UploadButton
            inputProps={getInputProps()}
            title={`Replace ${props.objectType}`}
            inputContainerProps={getRootProps({ className: "dropzone" })}
            
          >
            Drag 'n' drop some files here, or click to select files
          </UploadButton>
          <HousingAndMajorsReplacementModal
            isOpen={uploadModalShown}
            onClose={() => {
              setUploadModalShown(false);
            }}
            subnetwork={subnetwork}
            objectType={props.objectType}
            onSuccess={() => {
              setUploadModalShown(false);
              loadTypeData();
            }}
            values={uploadValues}
          />
        </Grid>
      )}
    </Grid>
  );
};
